<!-- eslint-disable vue/multi-word-component-names -->
<!-- eslint-disable no-undef -->
<script setup>
  import { ref } from 'vue';
  import { HomeIcon, CreditCardIcon, Squares2X2Icon, Cog6ToothIcon } from '@heroicons/vue/24/outline';

  defineOptions({ name: 'DefaultSidebar' })

  const isMobileMenuOpen = ref(false);

  const toggleMobileMenu = () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value;
  }

  defineExpose({ toggleMobileMenu })
</script>

<template>
  <!-- Desktop Sidebar — starts below the header (4px stripe + 80px = 84px) -->
  <aside
    class="sidebar hidden md:flex flex-col fixed top-[84px] left-0 h-[calc(100vh-84px)] w-[60px] hover:w-52 bg-mc-ink border-r border-t border-line-on-dark z-[19] transition-[width] duration-300 ease-in-out overflow-hidden"
  >
    <!-- Primary nav links -->
    <div class="flex flex-col flex-1 gap-1 p-2 pt-3 overflow-hidden">
      <RouterLink to="/dashboard" class="sidebar-link" title="Home">
        <span class="sidebar-icon">
          <HomeIcon class="w-5 h-5" aria-hidden="true" />
        </span>
        <span class="sidebar-fade sidebar-label">Home</span>
      </RouterLink>

      <RouterLink to="/sim-cards" class="sidebar-link" title="SIM Cards">
        <span class="sidebar-icon">
          <CreditCardIcon class="w-5 h-5" aria-hidden="true" />
        </span>
        <span class="sidebar-fade sidebar-label">SIM Cards</span>
      </RouterLink>

      <div class="my-1 border-t border-line-on-dark shrink-0"></div>

      <RouterLink to="/applications" class="sidebar-link" title="Cloud Apps">
        <span class="sidebar-icon">
          <Squares2X2Icon class="w-5 h-5" aria-hidden="true" />
        </span>
        <span class="sidebar-fade sidebar-label">Cloud Apps</span>
      </RouterLink>
    </div>

    <!-- Settings pinned to the bottom -->
    <div class="p-2 pb-3 border-t border-line-on-dark shrink-0">
      <RouterLink to="/settings" class="sidebar-link" title="Settings">
        <span class="sidebar-icon">
          <Cog6ToothIcon class="w-5 h-5" aria-hidden="true" />
        </span>
        <span class="sidebar-fade sidebar-label">Settings</span>
      </RouterLink>
    </div>
  </aside>

  <!-- Mobile overlay backdrop -->
  <div
    v-if="isMobileMenuOpen"
    class="md:hidden fixed inset-0 bg-mc-ink/70 z-40 backdrop-blur-sm"
    @click="toggleMobileMenu"
    aria-hidden="true"
  ></div>

  <!-- Mobile Navigation Drawer -->
  <nav
    class="md:hidden fixed top-0 left-0 w-[80%] max-w-[280px] h-screen z-50 bg-mc-ink border-r border-line-on-dark transform transition-transform duration-300 ease-in-out"
    :class="isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'"
    aria-label="Mobile navigation"
  >
    <!-- Red top accent stripe -->
    <div class="h-1 bg-mc-red"></div>

    <!-- Drawer header with brand and close button -->
    <div class="flex items-center justify-between px-5 py-5 border-b border-line-on-dark">
      <div class="flex items-center gap-3">
        <img
          src="@/assets/images/meshcomm-secondary-round-white.png"
          alt="Meshcomm Engineering"
          class="w-8 h-8 object-contain shrink-0"
        />
        <div class="flex flex-col leading-tight">
          <span class="font-display font-bold text-sm text-mc-white">MeshComm</span>
          <span class="font-mono text-[0.6rem] tracking-[0.12em] uppercase text-on-dark-muted">Portal</span>
        </div>
      </div>
      <button
        type="button"
        @click="toggleMobileMenu"
        class="flex items-center justify-center w-11 h-11 rounded-full text-on-dark hover:bg-white/10 transition-colors duration-160"
        aria-label="Close navigation"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Drawer nav items -->
    <div class="flex flex-col h-[calc(100%-81px)] overflow-y-auto">
      <div class="flex-1 p-4 space-y-1">
        <RouterLink @click="toggleMobileMenu" to="/dashboard" class="mobile-link">
          <HomeIcon class="w-5 h-5 shrink-0" aria-hidden="true" />
          <span>Home</span>
        </RouterLink>

        <RouterLink @click="toggleMobileMenu" to="/sim-cards" class="mobile-link">
          <CreditCardIcon class="w-5 h-5 shrink-0" aria-hidden="true" />
          <span>SIM Cards</span>
        </RouterLink>

        <div class="border-t border-line-on-dark my-2"></div>

        <RouterLink @click="toggleMobileMenu" to="/applications" class="mobile-link">
          <Squares2X2Icon class="w-5 h-5 shrink-0" aria-hidden="true" />
          <span>Cloud Apps</span>
        </RouterLink>
      </div>

      <div class="p-4 border-t border-line-on-dark">
        <RouterLink @click="toggleMobileMenu" to="/settings" class="mobile-link">
          <Cog6ToothIcon class="w-5 h-5 shrink-0" aria-hidden="true" />
          <span>Settings</span>
        </RouterLink>
      </div>
    </div>
  </nav>
</template>

<style scoped>
/* ── Desktop sidebar link ──────────────────────────────── */
.sidebar-link {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  border-radius: 12px; /* mc-sm */
  color: rgba(255, 255, 255, 0.82); /* on-dark */
  text-decoration: none;
  min-height: 44px;
  transition: background-color 160ms ease, color 160ms ease;
}

.sidebar-link:hover {
  background-color: rgba(255, 255, 255, 0.08);
  color: #ffffff;
}

.sidebar-link:focus-visible {
  outline: 3px solid #ffffff;
  outline-offset: 2px;
}

/* Vue Router injects .router-link-active on the matching link */
.sidebar-link.router-link-active {
  background-color: rgba(205, 33, 39, 0.18);
  color: #ffffff;
}

.sidebar-link.router-link-active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 20%;
  bottom: 20%;
  width: 3px;
  background-color: #cd2127;
  border-radius: 0 2px 2px 0;
}

/* ── Icon container ─────────────────────────────────────── */
.sidebar-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

/* ── Label ─────────────────────────────────────────────── */
.sidebar-label {
  font-family: 'Poppins', 'Inter', Arial, sans-serif;
  font-weight: 500;
  font-size: 0.8125rem;
  white-space: nowrap;
  color: rgba(255, 255, 255, 0.82);
}

/* Items that fade in when the sidebar is hovered/expanded */
.sidebar-fade {
  opacity: 0;
  transition: opacity 180ms ease;
}

.sidebar:hover .sidebar-fade {
  opacity: 1;
}

/* ── Mobile nav link ───────────────────────────────────── */
.mobile-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.82);
  font-family: 'Poppins', 'Inter', Arial, sans-serif;
  font-weight: 500;
  font-size: 0.9375rem;
  text-decoration: none;
  min-height: 44px;
  transition: background-color 160ms ease, color 160ms ease;
  border-left: 3px solid transparent;
}

.mobile-link:hover {
  background-color: rgba(255, 255, 255, 0.08);
  color: #ffffff;
}

.mobile-link:focus-visible {
  outline: 3px solid #ffffff;
  outline-offset: 2px;
}

.mobile-link.router-link-active {
  background-color: rgba(205, 33, 39, 0.18);
  border-left-color: #cd2127;
  color: #ffffff;
}

/* ── Reduced motion ────────────────────────────────────── */
@media (prefers-reduced-motion: reduce) {
  .sidebar,
  .sidebar-link,
  .sidebar-fade,
  .mobile-link {
    transition-duration: 0.001ms !important;
  }
}
</style>
